import { CaretDownOutlined, PauseCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { KitchenHoldTime } from "./kitchenHoldItems";
import {
  KitchenHoldTimeOptions,
  KitchenHoldTimeText,
  kitchenHoldTimeLabel,
} from "./KitchenHoldTimeOptions";

/** Времето на ред в „Преглед на поръчката" (`OrderPanelRow`). */
export interface KitchenHoldRowProps {
  time: KitchenHoldTime;
  pickerOpen: boolean;
  onTogglePicker: () => void;
  onPick: (time: KitchenHoldTime) => void;
  /** „Същото време за всички редове" — времето на реда за всички. */
  onApplyToAll: (time: KitchenHoldTime) => void;
}

/**
 * Бутонът с времето на реда — до степера, със същата височина, с ⏸ пред
 * времето. „Сега" е неутрален; минутите — запълнени в акцента; ∞ (ръчно) —
 * рамка и текст в акцента. `compact` — тесният панел (под 1440px), където
 * редът с бройката, бутона и степера иначе не се събира: само надписът.
 */
export const KitchenHoldRowChip = ({
  time,
  pickerOpen,
  onTogglePicker,
  compact = false,
}: Pick<KitchenHoldRowProps, "time" | "pickerOpen" | "onTogglePicker"> & {
  compact?: boolean;
}) => {
  const { t } = useTranslation();
  const accessibleLabel = `${t("ordering.kitchenHold.rowTime")}: ${kitchenHoldTimeLabel(time, t)}`;
  // Литерали, не сглобени класове — Tailwind ги търси в изходния код.
  const look =
    time === "now"
      ? "bg-pos-card border-pos-ctl text-pos-ink2"
      : time === "manual"
        ? "bg-pos-card border-pos-accent text-pos-accent"
        : "bg-pos-marked border-pos-accent text-pos-accent";
  return (
    <button
      type="button"
      onClick={onTogglePicker}
      aria-expanded={pickerOpen}
      aria-label={accessibleLabel}
      title={accessibleLabel}
      className={`shrink-0 h-8 rounded-[9px] border inline-flex items-center font-semibold whitespace-nowrap cursor-pointer transition-colors ${
        compact ? "px-1.5 text-[12px]" : "px-2 gap-1 text-[13px]"
      } ${look}`}
    >
      {!compact && (
        <PauseCircleOutlined className={`text-[11px] ${time === "now" ? "text-pos-dim" : ""}`} />
      )}
      <KitchenHoldTimeText time={time} infinityClassName="w-[18px]" />
      {!compact && <CaretDownOutlined className="text-[8px] opacity-60" />}
    </button>
  );
};

/** Разгънатият избор под реда: седемте времена + „Същото време за всички
 *  редове" (прилага времето на реда към всички). */
export const KitchenHoldRowPicker = ({
  time,
  onPick,
  onApplyToAll,
}: Pick<KitchenHoldRowProps, "time" | "onPick" | "onApplyToAll">) => {
  const { t } = useTranslation();
  return (
    <div className="border-t border-pos-line3 pt-2 flex flex-col gap-1.5">
      <KitchenHoldTimeOptions selected={time} onPick={onPick} />
      <button
        type="button"
        onClick={() => onApplyToAll(time)}
        className="self-end border-0 bg-transparent p-0 text-[12px] font-semibold text-pos-accent-ink cursor-pointer"
      >
        {t("ordering.kitchenHold.sameForAll")}
      </button>
    </div>
  );
};
